/**
 * Phase 3.28 — WhitelabelEmbedSection — Inserate per iFrame in eigene Webseite.
 */
import { useEffect, useState, useCallback } from "react";
import { Code2, Copy, Eye, ExternalLink, Globe, Link as LinkIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { useAutosave } from "../../hooks/useAutosave";
import SaveStatusIndicator from "./SaveStatusIndicator";

export function WhitelabelEmbedSection({ currentUser }) {
    const { refresh } = useAuth();
    const [form, setForm] = useState({
        embed_enabled: !!currentUser.embed_enabled,
        embed_logo_url: currentUser.embed_logo_url || "",
        embed_primary_color: currentUser.embed_primary_color || "#FFCC00",
        embed_click_behavior: currentUser.embed_click_behavior || "platform",
        embed_dealer_url_template: currentUser.embed_dealer_url_template || "",
        embed_allowed_domains_text: (currentUser.embed_allowed_domains || []).join("\n"),
    });
    const [previewKey, setPreviewKey] = useState(0);

    const backendBase = process.env.REACT_APP_BACKEND_URL || "";
    const dealerId = currentUser.id;
    const embedHtmlUrl = `${backendBase}/api/whitelabel/${dealerId}/embed.html`;
    const widgetJsUrl = `${backendBase}/api/whitelabel/${dealerId}/widget.js`;
    const jsonFeedUrl = `${backendBase}/api/whitelabel/${dealerId}/listings.json`;

    const iframeSnippet = `<iframe src="${embedHtmlUrl}" style="width:100%;min-height:680px;border:0" loading="lazy" title="Fahrzeuge"></iframe>`;
    const jsSnippet = `<div id="twowheels-embed-${dealerId}"></div>
<script async src="${widgetJsUrl}"></script>`;

    // Jun-2026 (User-Wunsch): Autospeichern mit grüner „Gespeichert"-Anzeige.
    const autosave = useCallback(async (data) => {
        const allowed = data.embed_allowed_domains_text
            .split(/[\n,]/)
            .map((d) => d.trim())
            .filter(Boolean);
        await api.patch("/users/me/dealer-profile", {
            embed_enabled: !!data.embed_enabled,
            embed_logo_url: data.embed_logo_url.trim() || null,
            embed_primary_color: data.embed_primary_color || "#FFCC00",
            embed_click_behavior: data.embed_click_behavior,
            embed_dealer_url_template: data.embed_dealer_url_template.trim() || null,
            embed_allowed_domains: allowed,
        });
        await refresh();
        setPreviewKey((k) => k + 1);
    }, [refresh]);
    const sig = useAutosave(form, autosave, { delay: 1000 });

    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} kopiert`);
        } catch {
            toast.error("Kopieren fehlgeschlagen — bitte manuell markieren");
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="whitelabel-section">
            <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                    <Code2 className="w-5 h-5 text-[#111827]" />
                    <h3 className="font-display font-bold text-lg">Homepage</h3>
                </div>
                <span data-testid="whitelabel-save-status">
                    <SaveStatusIndicator saving={sig.saving} dirty={sig.dirty} savedAt={sig.savedAt} />
                </span>
            </div>
            <p className="text-xs text-gray-600 mb-4">
                Zeigen Sie Ihre Inserate direkt auf Ihrer eigenen Webseite an — per iframe, JS-Widget oder JSON-Feed. Privat-Inserate sowie verkaufte Fahrzeuge werden ausgeblendet.
            </p>

            <label className="flex items-start gap-3 cursor-pointer mb-5" data-testid="embed-enabled-label">
                <input
                    type="checkbox"
                    checked={form.embed_enabled}
                    onChange={(e) => setForm({ ...form, embed_enabled: e.target.checked })}
                    className="mt-1 w-5 h-5 accent-[#FFCC00]"
                    data-testid="embed-enabled-checkbox"
                />
                <div className="flex-1">
                    <div className="font-bold text-[#111827]">Homepage-Verknüpfung aktivieren</div>
                    <div className="text-xs text-gray-600 mt-1">
                        Solange diese Option deaktiviert ist, liefern die Embed-URLs einen 403 zurück.
                    </div>
                </div>
            </label>

            {form.embed_enabled && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Logo-URL</Label>
                            <Input
                                value={form.embed_logo_url}
                                onChange={(e) => setForm({ ...form, embed_logo_url: e.target.value })}
                                placeholder="https://ihre-domain.de/logo.png"
                                className="rounded-sm mt-1 font-mono-num text-sm"
                                data-testid="embed-logo-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Primärfarbe</Label>
                            <div className="flex gap-2 mt-1">
                                <input
                                    type="color"
                                    value={form.embed_primary_color}
                                    onChange={(e) => setForm({ ...form, embed_primary_color: e.target.value })}
                                    className="h-10 w-12 rounded-sm border border-gray-300 cursor-pointer"
                                    data-testid="embed-color-picker"
                                />
                                <Input
                                    value={form.embed_primary_color}
                                    onChange={(e) => setForm({ ...form, embed_primary_color: e.target.value })}
                                    placeholder="#FFCC00"
                                    className="rounded-sm font-mono-num text-sm"
                                    data-testid="embed-color-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-4">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Klick-Verhalten</Label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                            {[
                                { v: "platform", t: "Zur Plattform (neuer Tab)", h: "Öffnet das Inserat auf TwoWheels" },
                                { v: "dealer", t: "Zur eigenen Webseite", h: "Verwendet Ihre URL-Vorlage unten" },
                                { v: "modal", t: "Im Embed (gleicher Tab)", h: "Öffnet innerhalb des iframes" },
                            ].map((opt) => (
                                <label
                                    key={opt.v}
                                    className={`flex items-start gap-2 p-3 border rounded-sm cursor-pointer ${form.embed_click_behavior === opt.v ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200 hover:border-gray-400"}`}
                                    data-testid={`embed-click-${opt.v}`}
                                >
                                    <input
                                        type="radio"
                                        name="embed_click_behavior"
                                        value={opt.v}
                                        checked={form.embed_click_behavior === opt.v}
                                        onChange={() => setForm({ ...form, embed_click_behavior: opt.v })}
                                        className="mt-0.5 accent-[#FFCC00]"
                                    />
                                    <div>
                                        <div className="font-bold text-sm">{opt.t}</div>
                                        <div className="text-[11px] text-gray-500">{opt.h}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {form.embed_click_behavior === "dealer" && (
                        <div className="mt-4">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">URL-Vorlage Ihrer Webseite</Label>
                            <Input
                                value={form.embed_dealer_url_template}
                                onChange={(e) => setForm({ ...form, embed_dealer_url_template: e.target.value })}
                                placeholder="https://ihre-domain.de/fahrzeug/{listing_id}"
                                className="rounded-sm mt-1 font-mono-num text-sm"
                                data-testid="embed-dealer-url-template"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Platzhalter: <code className="bg-gray-100 px-1 rounded">{"{listing_id}"}</code> wird ersetzt.
                            </p>
                        </div>
                    )}

                    <div className="mt-4">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Erlaubte Domains</Label>
                        <textarea
                            value={form.embed_allowed_domains_text}
                            onChange={(e) => setForm({ ...form, embed_allowed_domains_text: e.target.value })}
                            placeholder={"ihre-domain.de\nshop.ihre-domain.de\n*.partner.de"}
                            rows={3}
                            className="w-full border border-gray-200 rounded-sm mt-1 px-3 py-2 text-sm font-mono-num focus:outline-none focus:border-[#FFCC00]"
                            data-testid="embed-allowed-domains"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">
                            Eine Domain pro Zeile (oder Komma-getrennt). Leer lassen = überall einbettbar. Wildcards mit <code className="bg-gray-100 px-1 rounded">*.</code> möglich.
                        </p>
                    </div>

                    <p className="text-[11px] text-gray-500 mt-3" data-testid="whitelabel-autosave-hint">Änderungen werden automatisch gespeichert.</p>

                    {currentUser.embed_enabled && (
                        <div className="mt-6 pt-6 border-t border-gray-200 space-y-5" data-testid="embed-snippets">
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2">
                                        <Code2 className="w-4 h-4" /> iframe-Snippet
                                    </Label>
                                    <Button onClick={() => copy(iframeSnippet, "iframe-Snippet")} variant="outline" size="sm" className="rounded-sm h-7 text-xs font-bold border-[#111827]" data-testid="copy-iframe-btn">
                                        <Copy className="w-3 h-3 mr-1" /> Kopieren
                                    </Button>
                                </div>
                                <pre className="bg-[#111827] text-[#FFCC00] text-xs p-3 rounded-sm overflow-x-auto font-mono-num">{iframeSnippet}</pre>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2">
                                        <Code2 className="w-4 h-4" /> JS-Widget
                                    </Label>
                                    <Button onClick={() => copy(jsSnippet, "JS-Widget")} variant="outline" size="sm" className="rounded-sm h-7 text-xs font-bold border-[#111827]" data-testid="copy-js-btn">
                                        <Copy className="w-3 h-3 mr-1" /> Kopieren
                                    </Button>
                                </div>
                                <pre className="bg-[#111827] text-[#FFCC00] text-xs p-3 rounded-sm overflow-x-auto font-mono-num whitespace-pre">{jsSnippet}</pre>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2">
                                        <LinkIcon className="w-4 h-4" /> JSON-Feed (für eigene Templates)
                                    </Label>
                                    <Button onClick={() => copy(jsonFeedUrl, "JSON-Feed URL")} variant="outline" size="sm" className="rounded-sm h-7 text-xs font-bold border-[#111827]" data-testid="copy-json-btn">
                                        <Copy className="w-3 h-3 mr-1" /> Kopieren
                                    </Button>
                                </div>
                                <pre className="bg-[#111827] text-[#FFCC00] text-xs p-3 rounded-sm overflow-x-auto font-mono-num">{jsonFeedUrl}</pre>
                            </div>

                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2 mb-2">
                                    <Eye className="w-4 h-4" /> Live-Vorschau
                                </Label>
                                <iframe
                                    key={previewKey}
                                    src={embedHtmlUrl}
                                    title="Whitelabel Vorschau"
                                    className="w-full border border-gray-200 rounded-sm bg-white"
                                    style={{ height: 520 }}
                                    data-testid="embed-preview-iframe"
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
